// ======================================================
// LexCallAngelApp.jsx  – 2025-07-08 full edition (v4)
// ======================================================

import React, { useState, useEffect } from 'react';
import {
  Routes, Route, NavLink, useParams, useNavigate,
} from 'react-router-dom';
import {
  FiPhoneCall, FiPlus, FiUsers, FiFileText,
  FiMic, FiClock,
} from 'react-icons/fi';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import cityTz from 'city-timezones';
import './LexCallAngel.css';
import AngelWrapper from './angel';

import {
  auth, loadLcaData, saveLcaData, uploadAudio,
} from '../firebase';

/* ---------- utils ---------- */
const uuid     = () => `${Date.now()}-${Math.random().toString(36).slice(2,10)}`;
const tzByCity = c => cityTz.lookupViaCity(c)[0]?.timezone || 'UTC';
const API      = process.env.REACT_APP_API_URL || 'https://api.lextrackai.com';

/** 把 audio Blob 剥掉，避免写进 Firestore */
const stripBlob = obj => {
  if (!obj) return obj;
  const { audio, ...rest } = obj;
  return rest;
};

/* ---------- Context ---------- */
const AppCtx = React.createContext();

function AppProvider({ children }) {
  const [autoCalls, setAutoCalls] = useState([]);
  const [contacts,  setContacts ] = useState([]);
  const [notes,     setNotes    ] = useState([{ id: uuid(), text: 'Daily agenda' }]);

  const addCall    = c      => setAutoCalls(l => [...l, { ...stripBlob(c), id: uuid() }]);
  const updateCall = (id,u) => setAutoCalls(l => l.map(c => c.id === id ? { ...c, ...stripBlob(u) } : c));
  const deleteCall = id     => setAutoCalls(l => l.filter(c => c.id !== id));
  const saveContact = (name, cc, phone) => {
    if (!contacts.find(c => c.cc === cc && c.phone === phone))
      setContacts(l => [...l, { id: uuid(), name, cc, phone }]);
  };

  /* Firestore ⬇️ 载入 */
  useEffect(() => {
    if (!auth.currentUser) return;
    loadLcaData().then(d => {
      if (d) {
        setAutoCalls(d.autoCalls || []);
        setContacts (d.contacts  || []);
        setNotes    (d.notes     || []);
      }
    });
  }, [auth.currentUser?.uid]);

  /* Firestore ⬆️ 自动保存（500 ms debounce） */
  useEffect(() => {
    if (!auth.currentUser) return;
    const t = setTimeout(() => {
      saveLcaData({
        autoCalls: autoCalls.map(stripBlob),
        contacts,
        notes,
      }).catch(console.error);
    }, 500);
    return () => clearTimeout(t);
  }, [autoCalls, contacts, notes, auth.currentUser?.uid]);

  return (
    <AppCtx.Provider value={{
      autoCalls, contacts, notes,
      addCall, updateCall, deleteCall,
      saveContact, setNotes,
    }}>
      {children}
    </AppCtx.Provider>
  );
}

/* ---------- Layout + Tabs ---------- */
function Layout() {
  return (
    <div className="lca-layout">
      <BottomTabBar/>
      <main className="lca-content">
        <Routes>
          <Route index element={<HomePage/>}/>
          <Route path="create" element={<CreatePage/>}/>
          <Route path="edit/:id" element={<EditPage/>}/>
          <Route path="contacts" element={<ContactsPage/>}/>
          <Route path="notes" element={<NotesPage/>}/>
        </Routes>
      </main>
    </div>
  );
}
const BottomTabBar = () => (
  <nav className="lca-tabbar">
    {[
      ['',         FiPhoneCall, 'Auto Calls'],
      ['contacts', FiUsers,     'Contacts'  ],
      ['notes',    FiFileText,  'Notes'     ],
    ].map(([to,Icon,label]) => (
      <NavLink key={to} to={to} end={to===''}
        className={({isActive}) => isActive ? 'lca-tab active' : 'lca-tab'}>
        <Icon/><span>{label}</span>
      </NavLink>
    ))}
  </nav>
);

export default function LexCallAngelRoot() {
  return (
    <AngelWrapper>
      <AppProvider><Layout/></AppProvider>
    </AngelWrapper>
  );
}

/* ────────────────── 共用小组件 ────────────────── */
const DowPicker = ({ value, onChange }) => {
  const D = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <div className="dow-grid">
      {D.map((d,i)=>(
        <button key={d} type="button"
          className={value.includes(i)?'dow on':'dow'}
          onClick={()=>onChange(value.includes(i)?value.filter(v=>v!==i):[...value,i])}>
          {d}
        </button>
      ))}
    </div>
  );
};
const Recorder = ({ onSave }) => {
  const [rec,setRec] = useState(null);
  const [url,setUrl] = useState('');
  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio:true });
    const r = new MediaRecorder(stream); const chunks=[];
    r.ondataavailable = e => chunks.push(e.data);
    r.onstop = () => {
      const blob = new Blob(chunks,{type:'audio/webm'});
      const u = URL.createObjectURL(blob);
      setUrl(u); onSave(blob,u);
    };
    r.start(); setRec(r);
  };
  return (
    <>
      {rec
        ? <button type="button" className="lca-btn danger mt-16" onClick={()=>rec.stop()}>Stop</button>
        : <button type="button" className="lca-btn primary mt-16" onClick={start}><FiMic/> Record</button>}
      {url && <div className="mt-16"><audio controls src={url}/></div>}
    </>
  );
};

/* ---------- CallForm ---------- */
function CallForm({ initial={}, contacts, notes, onSubmit, onCancel }) {
  const [form,set] = useState({
    topic:'', contactId:contacts[0]?.id||'custom',
    customPhone:'', city:'', tz:'UTC', time:'08:00', repeat:[],
    msgType:'text', note:'', audio:null, audioUrl:'',
    ...initial,
  });
  const u=e=>set({...form,[e.target.name]:e.target.value});
  const pickNote = e => {
    const n = notes.find(n=>n.id===e.target.value);
    set(f=>({...f,note:n?.text||''}));
  };
  const onRec = blob => set(f=>({...f,audio:blob}));

  return (
    <div className="lca-form">
      <label>Topic</label>
      <input name="topic" value={form.topic} onChange={u}
             placeholder="Morning Wake-up call…"/>

      {/* Recipient */}
      <label className="mt-24">Recipient</label>
      <select name="contactId" value={form.contactId} onChange={u}>
        {contacts.map(c=>(
          <option key={c.id} value={c.id}>{c.name} ({c.cc}{c.phone})</option>
        ))}
        <option value="custom">↳ Enter custom number…</option>
      </select>
      {form.contactId==='custom'&&(
        <PhoneInput
          international defaultCountry="US"
          placeholder="Enter number"
          value={form.customPhone}
          onChange={v=>set(f=>({...f,customPhone:v}))}
          className="phone-input mt-8"
        />
      )}

      {/* City & time */}
      <label className="mt-24">Recipient City</label>
      <input name="city" value={form.city}
             onChange={e=>set(f=>({...f,city:e.target.value,tz:tzByCity(e.target.value)}))}
             placeholder="e.g. London"/>
      <p className="lca-card-note">Time-zone: {form.tz}</p>

      <label>Time</label>
      <div className="time-row" onClick={()=>document.getElementById('timeInp').showPicker?.()}>
        <FiClock className="time-ic"/>
        <input id="timeInp" name="time" type="time"
               value={form.time} onChange={u}/>
      </div>

      {/* repeat */}
      <label>Repeat Days</label>
      <DowPicker value={form.repeat} onChange={r=>set(f=>({...f,repeat:r}))}/>

      {/* msg */}
      <label className="mt-24">Message Type</label>
      <select name="msgType" value={form.msgType} onChange={u}>
        <option value="text">Text → TTS</option>
        <option value="audio">Upload Audio</option>
        <option value="record">Record Voice</option>
        <option value="note">Pick Note</option>
      </select>

      {form.msgType==='text'&&(
        <textarea name="note" value={form.note} onChange={u} placeholder="Write text…"/>
      )}
      {form.msgType==='audio'&&(
        <input type="file" accept="audio/*"
               onChange={e=>set(f=>({...f,audio:e.target.files[0]}))}/>
      )}
      {form.msgType==='record'&&<Recorder onSave={onRec}/>}
      {form.msgType==='note'&&(
        <>
          <select onChange={pickNote} defaultValue="">
            <option value="" disabled>Select a note</option>
            {notes.map(n=>(
              <option key={n.id} value={n.id}>{n.text.slice(0,30)}</option>
            ))}
          </select>
          {form.note&&<p className="lca-card-note mt-8">✔ chosen.</p>}
        </>
      )}

      <button type="button" className="lca-btn primary mt-24"
              onClick={()=>onSubmit(form)}>Save</button>
      <button type="button" className="lca-btn" onClick={onCancel}>Cancel</button>
    </div>
  );
}

/* ──────────────────── Pages ──────────────────── */
function HomePage() {
  const { autoCalls } = React.useContext(AppCtx);
  const nav = useNavigate();
  return (
    <section className="lca-section">
      <header className="lca-section-header">
        <h2>LEX – (888) 884-5281<br/><br/>The right call, right on time.</h2>
        <button type="button" className="lca-iconbtn" onClick={()=>nav('create')}><FiPlus/></button>
      </header>

      {autoCalls.length===0 ? <EmptyState/> : autoCalls.map(c=><CallCard key={c.id} call={c}/>)}
    </section>
  );
}
const EmptyState = () => (
  <div className="lca-empty">
    <div style={{textAlign:'center',color:'#fff'}}>
      <span style={{display:'block',fontWeight:800,marginBottom:'.5em'}}>Use Cases</span>
      <ul style={{textAlign:'left',margin:'0 1.25em',padding:0,listStyle:'disc'}}>
        <li>Morning action-item call</li><li>Pep-talk wake-up</li>
        <li>Birthday wish</li><li>Client follow-up</li>
        <li>Break-time reminder</li><li>Playful “Angel” call</li>
      </ul>
    </div>
  </div>
);
const CallCard = ({call}) => {
  const nav = useNavigate();
  return (
    <div className="lca-card" onClick={()=>nav(`edit/${call.id}`)}>
      <div className="lca-card-row">
        <span className="lca-card-date">
          {call.repeat?.length ? `⟳ ${call.repeat.length}d` : 'Once'}
        </span>
        <span className="lca-card-time">{call.time}</span>
      </div>
      <div className="lca-card-title">{call.topic || 'Untitled'}</div>
      <div className="lca-card-note">
        Call to <strong>{call.contactName || call.to}</strong>
      </div>
      {call.note && <div className="lca-card-note">{call.note}</div>}
    </div>
  );
};

/* ---------- Create ---------- */
function CreatePage() {
  const { contacts, notes, addCall } = React.useContext(AppCtx);
  const nav = useNavigate();

  const submit = async data => {
    /* ① 构造 to / contactName */
    let to='', contactName='';
    if (data.contactId === 'custom') {
      if (!data.customPhone) { alert('Enter number'); return; }
      to=data.customPhone; contactName=data.customPhone;
    } else {
      const ct = contacts.find(c=>c.id===data.contactId);
      if (!ct) { alert('Select contact'); return; }
      to=`${ct.cc}${ct.phone}`; contactName=ct.name;
    }

    /* ② 上传音频（可选） */
    let audioUrl='';
    if (['audio','record'].includes(data.msgType) && data.audio) {
      try { audioUrl = await uploadAudio(data.audio); }
      catch(e){ console.error(e); alert('Audio upload failed – try again'); return; }
    }

    /* ③ 写入 state → 跳首页 */
    addCall({ ...data, to, contactName, audioUrl });
    nav('/autocall');
  };

  return (
    <section className="lca-section">
      <header className="lca-section-header"><h2>New Auto Call</h2></header>
      <CallForm contacts={contacts} notes={notes}
                onSubmit={submit} onCancel={()=>nav(-1)}/>
    </section>
  );
}

/* ---------- Edit ---------- */
function EditPage() {
  const { id } = useParams();
  const { autoCalls, contacts, notes, updateCall, deleteCall } = React.useContext(AppCtx);
  const nav = useNavigate();
  const call = autoCalls.find(c=>c.id===id);
  if (!call) return <p>Not found</p>;

  const save = async data => {
    /* 更新号码 */
    let to=call.to, contactName=call.contactName;
    if (data.contactId==='custom') {
      if (!data.customPhone) { alert('Enter number'); return; }
      to=data.customPhone; contactName=data.customPhone;
    } else {
      const ct=contacts.find(c=>c.id===data.contactId);
      if (!ct) { alert('Select contact'); return; }
      to=`${ct.cc}${ct.phone}`; contactName=ct.name;
    }

    /* 上传音频（如有新录/选） */
    let audioUrl=call.audioUrl||'';
    if (['audio','record'].includes(data.msgType)&&data.audio) {
      try { audioUrl = await uploadAudio(data.audio); }
      catch(e){ console.error(e); alert('Audio upload failed – try again'); return; }
    }

    updateCall(id,{ ...data, to, contactName, audioUrl });
    nav(-1);
  };

  const testNow = async () => {
    await fetch(`${API}/api/instant-call`,{
      method:'POST', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({
        to:call.to, msgType:call.msgType,
        text:call.note, audioUrl:call.audioUrl||'',
      }),
    }).catch(console.error);
    alert('Dialling…');
  };
  const del = () => { deleteCall(id); nav('/autocall'); };

  return (
    <section className="lca-section">
      <header className="lca-section-header">
        <h2>Edit Call</h2>
        <div>
          <button type="button" className="lca-btn" onClick={testNow}>Test Now</button>
          <button type="button" className="lca-btn danger" onClick={del}>Delete</button>
        </div>
      </header>
      <CallForm initial={call} contacts={contacts} notes={notes}
                onSubmit={save} onCancel={()=>nav(-1)}/>
    </section>
  );
}

/* ---------- Contacts ---------- */
function ContactsPage() {
  const { contacts, saveContact } = React.useContext(AppCtx);
  const [d,set] = useState({ name:'', cc:'+1', phone:'' });
  const on=e=>set({...d,[e.target.name]:e.target.value});
  const add = () => {
    if (d.name && d.phone) saveContact(d.name,d.cc,d.phone);
    set({...d,name:'',phone:''});
  };
  return (
    <section className="lca-section">
      <header className="lca-section-header"><h2>Contacts</h2></header>
      <div className="lca-form">
        <label>Name</label><input name="name" value={d.name} onChange={on}/>
        <label>Country Code</label>
        <select name="cc" value={d.cc} onChange={on}>
          {['+1','+44','+86','+61'].map(cc=>(
            <option key={cc} value={cc}>{cc}</option>
          ))}
        </select>
        <label>Phone</label><input name="phone" value={d.phone} onChange={on}/>
        <button type="button" className="lca-btn primary" onClick={add}>Add</button>
      </div>
      {contacts.map(c=>(
        <div key={c.id} className="lca-card contact">
          <div className="lca-card-row">
            <span>{c.name}</span><span>{c.cc}{c.phone}</span>
          </div>
        </div>
      ))}
    </section>
  );
}

/* ---------- Notes ---------- */
function NotesPage() {
  const { notes, setNotes } = React.useContext(AppCtx);
  const [txt,setTxt] = useState('');
  const add = () => {
    if (txt.trim()) { setNotes(l=>[...l,{id:uuid(),text:txt}]); setTxt(''); }
  };
  return (
    <section className="lca-section">
      <header className="lca-section-header"><h2>Notes</h2></header>
      <div className="lca-form">
        <textarea value={txt} onChange={e=>setTxt(e.target.value)}
                  placeholder="Write note…"/>
        <button type="button" className="lca-btn primary" onClick={add}>Save Note</button>
      </div>
      {notes.map(n=>(
        <div key={n.id} className="lca-card">{n.text}</div>
      ))}
    </section>
  );
}
